import { ActionIcon, Alert, Flex, Text } from '@mantine/core';
import { IconAlertCircle, IconX } from '@tabler/icons-react';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect } from 'react';
import styles from './styles.module.css';

export interface ErrorType {
	type: 'error' | 'warning' | 'info';
	style: 'minimal' | 'full';
	title: string;
	details?: string;
	duration?: number;
	offset?: number;
}

export const ErrorHandler = ({
	error,
	setError,
}: {
	error: ErrorType | null;
	setError: React.Dispatch<React.SetStateAction<ErrorType | null>>;
}) => {
	useEffect(() => {
		if (error?.duration) {
			setTimeout(() => {
				setError(null);
			}, error.duration);
		}
	}, [error?.duration, setError]);

	return (
		<AnimatePresence>
			{error && error.style === 'full' && (
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0, transition: { delay: 0.2 } }}
					key={error.title}
					className={styles.fullErrorOverlay}
				>
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0, transition: { delay: 0.2 } }}
						exit={{ opacity: 0, y: 20 }}
						className={styles.fullErrorContent}
					>
						<Alert
							variant="filled"
							w="100%"
							color={error.type === 'error' ? 'red' : error.type === 'warning' ? 'orange' : 'blue'}
							withCloseButton
							onClose={() => setError(null)}
							title={error.title}
							icon={<IconAlertCircle />}
						>
							{error.details}
						</Alert>
					</motion.div>
				</motion.div>
			)}
			{error && error.style === 'minimal' && (
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0, transition: { delay: 0.2 } }}
					exit={{ opacity: 0, y: 20 }}
					key={error.title}
					className={styles.minimalErrorContainer}
					style={{ bottom: error.offset ?? 100 }}
				>
					<Flex
						p="xs"
						bg={error.type === 'error' ? 'red' : error.type === 'warning' ? 'orange' : 'blue'}
						className={styles.minimalErrorFlex}
						c="white"
						gap="sm"
						w="100%"
					>
						<IconAlertCircle size={18} />
						<Flex className={styles.minimalErrorMeta} flex={1} direction="column">
							<Text size="sm" fw={700}>
								{error.title}
							</Text>
							<Text size="xs">{error.details}</Text>
						</Flex>
						<Flex direction="column" align="flex-end" gap="xs">
							<ActionIcon bg="transparent" onClick={() => setError(null)}>
								<IconX size={18} color="white" />
							</ActionIcon>
						</Flex>
					</Flex>
				</motion.div>
			)}
		</AnimatePresence>
	);
};
